import type { HTMLAttributes, ReactNode } from 'react'
import { diffCharacters, type CharSegment } from '../lib/charDiff.ts'
import { diffLines } from '../lib/diff.ts'

/**
 * The two columns a conference of two sides comes down to: RECEITA on the
 * left, NO SISTEMA on the right.
 *
 * Ported from V1's `ComparisonColumnsBoard` and `DiffColumns`. The board is
 * tinted by what the check concluded, the header takes the same colour,
 * and lines that differ are coloured and bolded on both sides at once. A row
 * that agreed is left neutral: tinting everything would make the tint mean
 * nothing.
 */

/** What a check concluded, in the three bands the screen paints. */
/**
 * What a row is saying about itself.
 *
 * `resolved` is a divergence somebody has answered. It stays visible — the
 * difference is still real and still worth reading — but it stops shouting: a
 * board that keeps every answered row red gives a pharmacist no way to see
 * what is left, which is the only question they have once they start working
 * through one.
 */
export type ColumnStatus = 'error' | 'warning' | 'resolved' | 'no_issues'

const BOARD_STYLES: Record<
  ColumnStatus,
  { background: string; border: string; header: string; highlight: string }
> = {
  error: {
    background: 'var(--color-fill-error-weak)',
    border: 'var(--color-stroke-error-weak)',
    header: 'var(--color-text-error)',
    highlight: 'var(--color-text-error)',
  },
  warning: {
    background: 'var(--color-fill-warning-weak)',
    border: 'var(--color-stroke-warning-weak)',
    header: 'var(--color-text-warning)',
    highlight: 'var(--color-text-warning)',
  },
  resolved: {
    background: 'var(--color-fill-success-weak)',
    border: 'var(--color-stroke-success-weak)',
    header: 'var(--color-text-success)',
    highlight: 'var(--color-text-success)',
  },
  no_issues: {
    background: 'transparent',
    border: 'var(--color-stroke-weak)',
    header: 'var(--color-text-weak)',
    highlight: 'var(--color-text-strong)',
  },
}

export function headerColorFor(status: ColumnStatus): string {
  return BOARD_STYLES[status].header
}

export function ComparisonBoard({
  status,
  children,
  className = '',
  fillRows = false,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  status: ColumnStatus
  children: ReactNode
  /**
   * Let the rows carry the fill instead of the board.
   *
   * A board of ingredients washed entirely red because one of them diverged,
   * so the row that needed looking at was the one thing the colour could not
   * point to. Where the rows each have their own decision, they paint
   * themselves and the board keeps only its border and header -- which is
   * still what draws the eye to the section.
   *
   * Off by default: a board holding one value has nothing finer to say, and
   * an uncoloured one would just look unexamined.
   */
  fillRows?: boolean
}) {
  const style = BOARD_STYLES[status]
  return (
    <div
      className={`flex flex-col overflow-hidden rounded-[8px] ${className}`}
      style={{
        ...(fillRows ? {} : { backgroundColor: style.background }),
        border: `1px solid ${style.border}`,
      }}
      {...props}
    >
      {children}
    </div>
  )
}

export function ComparisonRow({
  children,
  divided = false,
  className = '',
  ...props
}: HTMLAttributes<HTMLDivElement> & { divided?: boolean }) {
  return (
    <div
      /*
       * Pin, receita, sistema, decisão.
       *
       * Both controls get a column of their own rather than sharing one on the
       * right: reading and answering are different acts, and crowding them
       * into the same cell made the pair read as one widget. The pin leads
       * because it is about the row you are looking at; the decision closes
       * because it is what you do once you have looked.
       *
       * Fixed widths on both edges, so every board's columns line up whether
       * or not its rows can be pinned or decided.
       */
      className={`grid grid-cols-[1.5rem_1fr_1fr_auto] items-center gap-x-3 px-3 py-[3px]${
        divided ? ' border-t border-t-[rgba(0,13,77,0.06)]' : ''
      } ${className}`}
      {...props}
    >
      {children}
    </div>
  )
}

export function ComparisonHeader({
  status,
  headings = ['Receita', 'No sistema'],
}: {
  status: ColumnStatus
  /** The rótulo's rows compare other things: the receita or sistema, then the label. */
  headings?: readonly [string, string]
}) {
  const color = headerColorFor(status)
  return (
    <ComparisonRow>
      {/* The pin's column, empty in the header. Reserved rather than left to
          an absolute position: the icon used to float over the row and land
          on top of a long ingredient name. */}
      <span aria-hidden="true" />
      <span className="text-tSmall py-1 uppercase tracking-[0.04em]" style={{ color }}>
        {headings[0]}
      </span>
      <span className="text-tSmall py-1 uppercase tracking-[0.04em]" style={{ color }}>
        {headings[1]}
      </span>
      {/* And the decision's, likewise reserved. */}
      <span aria-hidden="true" className="w-[4.5rem]" />
    </ComparisonRow>
  )
}

export function DiffColumns({
  prescriptionValue,
  systemValue,
  status,
  pin,
  note,
  actions,
  headings,
  footer,
  characters = false,
}: {
  prescriptionValue: string
  systemValue: string
  status: ColumnStatus
  /** Column headings, when the two sides are not the receita and the sistema. */
  headings?: readonly [string, string] | undefined
  /**
   * Rendered in the row's leading column.
   *
   * Passed in rather than drawn here: this ingredient knows how to lay two
   * values side by side and nothing about what is pinned. Absent leaves the
   * column reserved and empty, which is what keeps every board's columns
   * lining up whether or not its rows can be pinned.
   */
  pin?: ReactNode
  /**
   * Rendered at the head of the receita column, before the value.
   *
   * Here rather than in the pin's column because the two answer different
   * questions and used to fight over one slot: a row that could not be checked
   * showed its reason *instead of* its pin, so the one row a reader most wants
   * held on the page was the one they could not hold.
   *
   * Drawn in a `ReasonSlot`, held open on every line of the value when there
   * is one and not at all when there is none, so a quiet row starts under its
   * heading.
   */
  note?: ReactNode
  /** Rendered in the trailing column: what you do once you have looked. */
  actions?: ReactNode
  /**
   * Rendered under the value, inside the board: what is said about this value
   * without being a side of it. Inside, because drawn after the board it read
   * as belonging to whatever came next.
   */
  footer?: ReactNode
  /**
   * Mark the letters that differ within a differing line, not only the line.
   * For names, where the difference is a letter or two; see `charDiff.ts` for
   * why nothing else asks for it.
   */
  characters?: boolean
}) {
  const style = BOARD_STYLES[status]
  const { prescriptionLines, systemLines } = diffLines(prescriptionValue, systemValue)
  const neutral = status === 'no_issues'
  const hasNote = note !== undefined && note !== null && note !== false

  return (
    <ComparisonBoard status={status}>
      <ComparisonHeader status={status} {...(headings === undefined ? {} : { headings })} />
      {prescriptionLines.map((line, index) => {
        const segments =
          characters && !neutral && line.differs
            ? diffCharacters(line.text, systemLines[index]!.text)
            : undefined
        return (
          <ComparisonRow key={index} divided>
            {/* Only on the first line, both of them: a value wrapped over three
              lines is one selectable thing, and three pins -- or three pairs
              of buttons -- would say otherwise. */}
            {index === 0 ? (pin ?? <span aria-hidden="true" />) : <span aria-hidden="true" />}
            <span className="flex min-w-0 items-baseline gap-1">
              {/* Filled on the first line only: a value wrapped over three lines
                is one thing with one note. */}
              {hasNote && <ReasonSlot>{index === 0 ? note : null}</ReasonSlot>}
              <DiffLine
                line={line}
                highlight={style.highlight}
                neutral={neutral}
                segments={segments?.prescription}
              />
            </span>
            <DiffLine
              line={systemLines[index]!}
              highlight={style.highlight}
              neutral={neutral}
              segments={segments?.system}
              system
            />
            <span className="flex w-[4.5rem] justify-end">{index === 0 ? actions : null}</span>
          </ComparisonRow>
        )
      })}
      {/*
        On the card's own surface, not the board's: the board is coloured by
        what the comparison found, and a reminder under an amber forma read as
        an amber reminder -- something to answer, which it is not.
      */}
      {footer !== undefined && footer !== null && footer !== false && (
        <div className="bg-background-raised border-t border-t-[rgba(0,13,77,0.06)]">{footer}</div>
      )}
    </ComparisonBoard>
  )
}

/**
 * The ⓘ's place at the head of the receita column, held open on every row.
 *
 * It used to take room only when a row had a reason, on the trade that quiet
 * rows keep a notch of their value. Galdino's review (#338) showed the cost:
 * on the ingredient list, where some rows carry an icon and their neighbours
 * do not, the names started at different places and the table looked broken.
 * A value that lines up with the row above it is worth more than the notch.
 *
 * Held open per board, not per screen: a board where no row has an icon drops
 * the slot altogether, so its values start under their heading instead of a
 * notch to the right of it for an icon that is never drawn.
 */
export function ReasonSlot({ children }: { children?: ReactNode }) {
  return (
    <span data-slot="reason" className="flex w-3.5 shrink-0 justify-start">
      {children}
    </span>
  )
}

function DiffLine({
  line,
  highlight,
  neutral,
  segments,
  system = false,
}: {
  line: { text: string; differs: boolean }
  highlight: string
  neutral: boolean
  /** The line cut by letter, when only some of it differs. */
  segments?: readonly CharSegment[] | undefined
  system?: boolean
}) {
  if (segments !== undefined && segments.length > 0) {
    return (
      <span
        className="text-tP text-text-strong min-w-0 py-1"
        data-side={system ? 'system' : 'prescription'}
      >
        {segments.map((segment, index) =>
          segment.differs ? (
            <mark
              key={index}
              data-differs="true"
              className="rounded-[2px] px-px"
              style={{
                color: highlight,
                fontWeight: 600,
                background: `color-mix(in srgb, ${highlight} 16%, transparent)`,
              }}
            >
              {segment.text}
            </mark>
          ) : (
            <span key={index}>{segment.text}</span>
          ),
        )}
      </span>
    )
  }
  return (
    <span
      className="text-tP min-w-0 py-1"
      data-side={system ? 'system' : 'prescription'}
      style={{
        color: neutral || !line.differs ? 'var(--color-text-strong)' : highlight,
        fontWeight: !neutral && line.differs ? 600 : 400,
      }}
    >
      {line.text || ' '}
    </span>
  )
}

/** The label above each board. */
export function SectionLabel({ label }: { label: string }) {
  return (
    <span className="text-tSmall text-text-weak font-medium uppercase tracking-[0.06em]">
      {label}
    </span>
  )
}
